import { useEffect, useState } from 'react';
import { Activity, Gavel, Landmark, ListChecks, Megaphone, RefreshCw, ShoppingBag, Users } from 'lucide-react';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { useUi } from '../context/UiContext';
import type { DashboardData } from '../types';
import { commissionStatus, formatMoney, formatTime } from '../utils';
import { Badge, Button, EmptyBlock, LoadingBlock, PageHeader, Panel, StatCard } from '../components/ui';

export function DashboardPage({ onNavigate }: { onNavigate: (view: 'commissions' | 'notices' | 'members') => void }) {
  const { user, settings, refresh } = useAuth();
  const { toast } = useUi();
  const [data, setData] = useState<DashboardData | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async (silent = false) => {
    if (!silent) setBusy(true);
    try {
      const next = await api<DashboardData>('/api/dashboard');
      setData(next);
      await refresh();
    } catch {
      toast('总览数据加载失败', 'error');
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    void load(true);
  }, [user?.id]);

  if (!data) return <LoadingBlock />;
  const stats = data.stats;

  return (
    <>
      <PageHeader
        title={'调度总览'}
        description={'这里汇总当前委托、市场、拍卖和公告状态。所有金额均使用 ' + settings.currency + ' 记账。'}
        action={<Button onClick={() => void load()}><RefreshCw size={16} /> 刷新</Button>}
      />

      <div className="hero-ledger">
        <div className="hero-copy">
          <span className="eyebrow">SERVER LEDGER / LIVE</span>
          <h2>服务器事务，集中处理。</h2>
          <p>委托资金由系统托管，市场实时结算，拍卖出价自动冻结。每个关键操作都会写入审计日志。</p>
        </div>
        <div className="hero-balance">
          <span>{user ? '我的可用余额' : '当前登录状态'}</span>
          <strong>{user ? formatMoney(user.balanceCents, settings.currency) : '未登录'}</strong>
          <small>{user ? '余额由后端账簿实时计算' : '登录后可发布委托、购买和出价'}</small>
        </div>
      </div>

      <div className="stats-grid">
        <StatCard label="活跃成员" value={stats.activeUsers} detail="状态正常的账号" />
        <StatCard label="待接委托" value={stats.openCommissions} detail={'进行中 ' + stats.workingCommissions + ' 项'} />
        <StatCard label="市场库存" value={stats.marketStock} detail="所有在售物品总数" />
        <StatCard label="进行中拍卖" value={stats.activeAuctions} detail={'公告 ' + stats.noticeCount + ' 条'} />
      </div>

      <div className="dashboard-grid">
        <Panel title="最新公告" eyebrow="NOTICE BOARD" action={<Button variant="ghost" onClick={() => onNavigate('notices')}>查看全部</Button>}>
          {data.notices.length ? (
            <div className="notice-list">
              {data.notices.map(notice => (
                <article className="notice-row" key={notice.id}>
                  <div className="notice-marker">{notice.pinned ? '置顶' : '公告'}</div>
                  <div>
                    <strong>{notice.title}</strong>
                    <p>{notice.content}</p>
                    <small>{notice.authorName} · {formatTime(notice.createdAt)}</small>
                  </div>
                </article>
              ))}
            </div>
          ) : <EmptyBlock title="暂无公告" description="管理员发布公告后会显示在这里。" action={<Button onClick={() => onNavigate('notices')}>查看公告栏</Button>} />}
        </Panel>

        <Panel title="最近委托" eyebrow="DISPATCH FEED" action={<Button variant="ghost" onClick={() => onNavigate('commissions')}>进入大厅</Button>}>
          {data.latestCommissions.length ? (
            <div className="compact-list">
              {data.latestCommissions.map(item => {
                const state = commissionStatus[item.status];
                return (
                  <div className="compact-row" key={item.id}>
                    <div><strong>{item.title}</strong><small>{item.creatorName} · {item.type}</small></div>
                    <div className="row-end"><b>{formatMoney(item.rewardCents, settings.currency)}</b><Badge tone={state.tone}>{state.label}</Badge></div>
                  </div>
                );
              })}
            </div>
          ) : <EmptyBlock title="还没有委托" description="发布第一笔委托后会出现在这里。" action={<Button variant="primary" onClick={() => onNavigate('commissions')}>进入委托大厅</Button>} />}
        </Panel>
      </div>

      <Panel title="托管资产" eyebrow="ESCROW">
        <div className="escrow-grid">
          <div><Landmark size={21} /><span>委托托管</span><strong>{formatMoney(stats.commissionEscrowCents, settings.currency)}</strong></div>
          <div><Gavel size={21} /><span>拍卖冻结</span><strong>{formatMoney(stats.auctionEscrowCents, settings.currency)}</strong></div>
          <div><Users size={21} /><span>活跃成员</span><strong>{stats.activeUsers} 人</strong></div>
          <div><Activity size={21} /><span>进行中事务</span><strong>{stats.openCommissions + stats.workingCommissions + stats.activeAuctions}</strong></div>
        </div>
      </Panel>
    </>
  );
}
